import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import CssBaseline from '@material-ui/core/CssBaseline'
import { ThemeProvider } from '@material-ui/core/styles'
import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'

import SimpleDialog from '../components/atoms/Dialog/simple'
import ScrollToTop from '../components/ScrollToTop'
import { authPages, publicPages } from '../config/menu'
import theme from '../config/theme'
import { LoginProvider } from '../store/useLogin'
import { log } from '../utils/log'
import { getSession } from '../utils/session'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, info) {
    log({
      errorType: 'unexpected_error',
      message: error?.message || 'Unexpected error',
      description: info?.componentStack || ''
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen">
          <p className="subheading-xs-regular">
            Terjadi kesalahan. Silakan muat ulang halaman.
          </p>
        </div>
      )
    }
    return this.props.children
  }
}

import AuthRouter from './AuthRouter'
import PrivateRoutes from './PrivateRoutes'
import PublicRoute from './PublicRouter'

const DownloadFilePage = lazy(() => import('../pages/download'))
const LoginPage = lazy(() => import('../pages/auth/login'))
const ForgotPasswordPage = lazy(() => import('../pages/auth/forgot-password'))
const ResetPasswordPage = lazy(() => import('../pages/auth/reset-password'))
const OnboardingPage = lazy(() => import('../pages/auth/onboarding'))
const OnboardingEmailPage = lazy(() => import('../pages/auth/onboarding-email'))
const OnboardingEmailOtpPage = lazy(
  () => import('../pages/auth/onboarding-email-otp')
)
const OnboardingPhonePage = lazy(() => import('../pages/auth/onboarding-phone'))
const OnboardingPhoneOtpPage = lazy(
  () => import('../pages/auth/onboarding-phone-otp')
)
const OnboardingPasswordPage = lazy(
  () => import('../pages/auth/onboarding-password')
)
const RiskProfilePage = lazy(() => import('../pages/auth/risk-profile'))
const RegisterPage = lazy(() => import('../pages/auth/register/'))
const RegisterSuccessPage = lazy(() => import('../pages/auth/register/success'))
const SetPasswordPage = lazy(() => import('../pages/auth/set-password'))
const DashboardPage = lazy(() => import('../pages/dashboard/home'))

// MF
// const ProductSearchPage = lazy(
//   () => import('../pages/dashboard/product/search')
// )
const ProductCompareMutualFundPage = lazy(
  () => import('../pages/dashboard/product-compare/mutualfund')
)
const MFProductDetailPage = lazy(
  () => import('../pages/dashboard/product/mutualfund/detail')
)
const PortfolioPage = lazy(() => import('../pages/dashboard/portfolio'))
const MutualFundProductPage = lazy(
  () => import('../pages/dashboard/product/mutualfund')
)
const MutualFundSubscriptionPage = lazy(
  () => import('../pages/dashboard/subscription/mutualfund')
)
const MutualFundRedemptionPage = lazy(
  () => import('../pages/dashboard/redemption/mutualfund')
)
const MutualFundRedemptionOtpPage = lazy(
  () => import('../pages/dashboard/redemption-otp/mutualfund')
)
const GoldRedemptionOtpPage = lazy(
  () => import('../pages/dashboard/redemption-otp/gold')
)
const GoldRedemptionSuccessPage = lazy(
  () => import('../pages/dashboard/redemption-success/gold')
)
const MutualFundRedemptionSuccessPage = lazy(
  () => import('../pages/dashboard/redemption-success/mutualfund')
)
// Gold
const GoldOnboardingPage = lazy(
  () => import('../pages/dashboard/gold-onboarding')
)
const GoldOnboardingFormPage = lazy(
  () => import('../pages/dashboard/gold-onboarding-form')
)
const GoldProductPage = lazy(() => import('../pages/dashboard/product/gold'))
const GoldSubscriptionPage = lazy(
  () => import('../pages/dashboard/subscription/gold')
)
const GoldRedemptionPage = lazy(
  () => import('../pages/dashboard/redemption/gold')
)
const GoldSyncPage = lazy(() => import('../pages/dashboard/gold-sync'))
// Payment
const PayReviewOrderPage = lazy(
  () => import('../pages/dashboard/pay-order-review/gold')
)
const PayOrderReviewMutualfundPage = lazy(
  () => import('../pages/dashboard/pay-order-review/mutualfund')
)
const PayTransferBankPage = lazy(
  () => import('../pages/dashboard/pay-transfer-bank/gold')
)
const PayTransferBankMutualfundPage = lazy(
  () => import('../pages/dashboard/pay-transfer-bank/mutualfund')
)
const PaySuccessPage = lazy(() => import('../pages/dashboard/pay-success'))
// Sell
const SellReviewOrderGoldPage = lazy(
  () => import('../pages/dashboard/sell-order-review/gold')
)
const SellReviewOrderMutualfundPage = lazy(
  () => import('../pages/dashboard/sell-order-review/mutualfund')
)
const TransactionPage = lazy(() => import('../pages/dashboard/transaction'))
const TransactionDetailMFPage = lazy(
  () => import('../pages/dashboard/transaction-detail/mutualfund')
)
const TransactionDetailGoldPage = lazy(
  () => import('../pages/dashboard/transaction-detail/gold')
)
// Account
const AccountPage = lazy(() => import('../pages/dashboard/account/'))
const ReminderPage = lazy(() => import('../pages/dashboard/reminder'))
const ReminderAddPage = lazy(() => import('../pages/dashboard/reminder/add'))
const ReminderEditPage = lazy(() => import('../pages/dashboard/reminder/edit'))
const ReminderSuccessPage = lazy(
  () => import('../pages/dashboard/reminder/success')
)
const SettingsPage = lazy(() => import('../pages/dashboard/settings'))
const NotFound = lazy(() => import('../pages/not-found'))
const OTPPassword = lazy(() => import('../pages/auth/otp-password'))
const OTPMethods = lazy(() => import('../pages/auth/otp-methods'))
const SelectInstitutionPage = lazy(
  () => import('../pages/dashboard/select-institution')
)

const AuthPages = Object.keys(authPages)
const PublicPages = Object.keys(publicPages)

const Components = {
  DownloadFilePage,
  LoginPage,
  RegisterPage,
  RegisterSuccessPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  SetPasswordPage,
  DashboardPage,
  // ProductPage, // deprecated
  MutualFundProductPage,
  MutualFundSubscriptionPage,
  MutualFundRedemptionPage,
  MutualFundRedemptionOtpPage,
  MutualFundRedemptionSuccessPage,
  // ProductSearchPage,
  ProductCompareMutualFundPage,
  MFProductDetailPage,
  PortfolioPage,
  TransactionPage,
  TransactionDetailMFPage,
  TransactionDetailGoldPage,
  AccountPage,
  ReminderAddPage,
  ReminderEditPage,
  ReminderSuccessPage,
  SettingsPage,
  ReminderPage,
  OTPPassword,
  OTPMethods,
  GoldProductPage,
  GoldOnboardingPage,
  GoldOnboardingFormPage,
  GoldSubscriptionPage,
  GoldRedemptionPage,
  GoldSyncPage,
  OnboardingPage,
  OnboardingEmailPage,
  OnboardingEmailOtpPage,
  OnboardingPhonePage,
  OnboardingPhoneOtpPage,
  OnboardingPasswordPage,
  RiskProfilePage,
  SelectInstitutionPage,
  PayReviewOrderPage,
  PayOrderReviewMutualfundPage,
  PayTransferBankPage,
  PayTransferBankMutualfundPage,
  PaySuccessPage,
  SellReviewOrderGoldPage,
  SellReviewOrderMutualfundPage,
  GoldRedemptionOtpPage,
  GoldRedemptionSuccessPage
}

const RouterFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="loading-spinner size-12" />
  </div>
)

const AppRouter = () => {
  console.log('Build Tag: ', process.env.REACT_APP_BUILD_TAG)

  const session = getSession('b-bfb')
  const sessionLogin = getSession('b-bfb-login')
  const now = new Date().getTime()

  const isAuthenticated = session ? now < session.expired : false
  const isOnLoginOTP = sessionLogin ? now < sessionLogin.expired : false

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <Suspense fallback={<RouterFallback />}>
            <Router basename={'/index.html#'}>
              <ScrollToTop />
              <LoginProvider history={history}>
                <Switch>
                  {PublicPages.map((key) => {
                    const page = publicPages[key]
                    return (
                      <PublicRoute
                        key={key}
                        isAuthenticated={isAuthenticated}
                        path={page.path}
                        component={Components[page.component]}
                        exact
                      />
                    )
                  })}
                  {AuthPages.map((key) => {
                    const page = authPages[key]
                    return (
                      <AuthRouter
                        key={key}
                        isAuthenticated={isOnLoginOTP}
                        path={page.path}
                        exact
                        name={page.name}
                        component={Components[page.component]}
                      />
                    )
                  })}
                  <Route path={process.env.REACT_APP_PREFIX}>
                    <PrivateRoutes components={Components} />
                  </Route>
                  <Route exact path="/download,:file">
                    <DownloadFilePage />
                  </Route>
                  <Route component={NotFound} />
                </Switch>
              </LoginProvider>
            </Router>
          </Suspense>
        </ErrorBoundary>
      </ThemeProvider>
    </React.Fragment>
  )
}

export default AppRouter
